import {
  CreateObjective,
  CreateStep,
  Objective,
  Step,
} from '../types/objective'
import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  loadObjective,
  loadObjectives,
  modifyObjective,
  removeObjective,
  saveObjective,
} from '../api/objective'
import { modifyStep, removeStep, saveStep } from '../api/step'
import { sortStepsByStatus } from '../utils/stepSort'
import { sortObjectivesByDefault } from '../utils/objectivesSorts'

const updateObjectiveInList = (
  objectives: Objective[],
  updated: Objective,
): Objective[] =>
  objectives.map((item) =>
    item.id === updated.id ? { ...item, ...updated } : item,
  )

const updateStepInObjective = (objective: Objective, step: Step): Objective => ({
  ...objective,
  steps: sortStepsByStatus(
    objective.steps.map((item) =>
      item.id === step.id
        ? { ...item, ...step, toDoes: step.toDoes ?? item.toDoes ?? [] }
        : item,
    ),
    'Active',
  ),
})

const appendStepToObjective = (objective: Objective, step: Step): Objective => ({
  ...objective,
  steps: sortStepsByStatus(
    [...objective.steps, { ...step, toDoes: step.toDoes ?? [] }],
    'Active',
  ),
})

const removeStepFromObjective = (
  objective: Objective,
  stepId: number,
): Objective => ({
  ...objective,
  steps: objective.steps.filter((item) => item.id !== stepId),
})

// get all objectives
export const getObjectives = createAsyncThunk(
  'objective/getObjectives',
  async (_, thunkApi) => {
    try {
      return await loadObjectives()
    } catch (e) {
      return thunkApi.rejectWithValue('objectiveError')
    }
  },
)

//get objective
export const getObjective = createAsyncThunk(
  'objective/getObjective',
  async (objectiveId: number, thunkApi) => {
    try {
      return await loadObjective(objectiveId)
    } catch (e) {
      return thunkApi.rejectWithValue('objectiveError')
    }
  },
)

export const refreshObjective = createAsyncThunk(
  'objective/refreshObjective',
  async (objectiveId: number, thunkApi) => {
    try {
      return await loadObjective(objectiveId)
    } catch (e) {
      return thunkApi.rejectWithValue('objectiveError')
    }
  },
)

// add objective
export const addObjective = createAsyncThunk(
  'objective/addObjective',
  async (objective: CreateObjective, thunkApi) => {
    try {
      return await saveObjective(objective)
    } catch (e) {
      return thunkApi.rejectWithValue('objectiveAddError')
    }
  },
)

// update objective status
export const updateObjective = createAsyncThunk(
  'objective/updateObjective',
  async (objective: Objective, thunkApi) => {
    try {
      return await modifyObjective(objective)
    } catch (e) {
      return thunkApi.rejectWithValue('objectiveError')
    }
  },
)

// delete objective
export const deleteObjective = createAsyncThunk(
  'objective/deleteObjective',
  async (id: number, thunkApi) => {
    try {
      return await removeObjective(id)
    } catch (e) {
      return thunkApi.rejectWithValue('objectiveError')
    }
  },
)

//save step
export const addStep = createAsyncThunk(
  'step/saveStep',
  async (step: CreateStep, thunkApi) => {
    try {
      return await saveStep(step)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//update step
export const updateStep = createAsyncThunk(
  'step/updateStep',
  async (step: Step, thunkApi) => {
    try {
      const updatedStep = await modifyStep(step)
      return updatedStep
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//delete step
export const deleteStep = createAsyncThunk(
  'step/deleteStep',
  async (stepId: number, thunkApi) => {
    try {
      const id = await removeStep(stepId)
      return id
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

interface ObjectiveSliceState {
  objectives: Objective[]
  objective?: Objective
  objectiveDetails?: Objective
  objectiveLoading: boolean
  objectiveError?: SerializedError
  stepLoading: boolean
  stepError?: SerializedError
}

const initialState: ObjectiveSliceState = {
  objectives: [],
  objective: undefined,
  objectiveDetails: undefined,
  objectiveLoading: false,
  objectiveError: undefined,
  stepLoading: false,
  stepError: undefined,
}

const objectiveSlice = createSlice({
  name: 'objective',
  initialState,
  reducers: {
    setObjectives: (state, action: PayloadAction<Objective[]>) => {
      //@ts-ignore
      state.objectives = action.payload
    },

    setObjective: (state, action: PayloadAction<Objective | undefined>) => {
      //@ts-ignore
      state.objective = action.payload
    },

    setObjectiveDetails: (
      state,
      action: PayloadAction<Objective | undefined>,
    ) => {
      //@ts-ignore
      state.objectiveDetails = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getObjectives.fulfilled, (
      state,
      action: PayloadAction<Objective[]>,
    ) => {
      //@ts-ignore
      state.objectives = sortObjectivesByDefault(action.payload)
      state.objectiveLoading = false
      state.objectiveError = undefined
    })
    builder.addCase(getObjectives.pending, (state) => {
      state.objectiveLoading = true
      state.objectiveError = undefined
    })
    builder.addCase(getObjectives.rejected, (state, action) => {
      state.objectiveLoading = false
      state.objectiveError = action.error
    })
    builder.addCase(getObjective.fulfilled, (
      state,
      action: PayloadAction<Objective>,
    ) => {
      if (!action.payload?.id) {
        state.objective = undefined
        state.objectiveLoading = false
        state.objectiveError = undefined
        return
      }
      const steps = sortStepsByStatus(action.payload.steps, 'Active')
      //@ts-ignore
      state.objective = { ...action.payload, steps }
      state.objectiveLoading = false
      state.objectiveError = undefined
    })
    builder.addCase(getObjective.pending, (state) => {
      state.objectiveLoading = true
      state.objectiveError = undefined
    })
    builder.addCase(getObjective.rejected, (state, action) => {
      state.objectiveLoading = false
      state.objectiveError = action.error
    })
    builder.addCase(refreshObjective.fulfilled, (
      state,
      action: PayloadAction<Objective>,
    ) => {
      //@ts-ignore
      state.objectives = state.objectives.map((objective: Objective) =>
        objective.id === action.payload.id ? action.payload : objective,
      )
      if (state.objectiveDetails?.id === action.payload.id) {
        state.objectiveDetails = { ...action.payload } as any
      }
      if (state.objective?.id === action.payload.id) {
        state.objective = { ...action.payload } as any
      }
      state.objectiveLoading = false
      state.objectiveError = undefined
    })
    builder.addCase(refreshObjective.pending, (state) => {
      state.objectiveLoading = true
      state.objectiveError = undefined
    })
    builder.addCase(refreshObjective.rejected, (state, action) => {
      state.objectiveLoading = false
      state.objectiveError = action.error
    })
    builder.addCase(addObjective.fulfilled, (
      state,
      action: PayloadAction<Objective>,
    ) => {
      const objectives = state.objectives
      //@ts-ignore
      objectives.push(action.payload)
      //@ts-ignore
      state.objectives = sortObjectivesByDefault(objectives)
      state.objectiveLoading = false
      state.objectiveError = undefined
    })
    builder.addCase(addObjective.pending, (state) => {
      state.objectiveLoading = true
      state.objectiveError = undefined
    })
    builder.addCase(addObjective.rejected, (state, action) => {
      state.objectiveLoading = false
      state.objectiveError = action.error
    })
    builder.addCase(deleteObjective.fulfilled, (
      state,
      action: PayloadAction<number>,
    ) => {
      state.objectives = state.objectives.filter(
        (objective) => objective.id !== action.payload,
      )
      state.objectiveLoading = false
      state.objectiveError = undefined
    })
    builder.addCase(deleteObjective.pending, (state) => {
      state.objectiveLoading = true
      state.objectiveError = undefined
    })
    builder.addCase(deleteObjective.rejected, (state, action) => {
      state.objectiveLoading = false
      state.objectiveError = action.error
    })
    builder.addCase(updateObjective.fulfilled, (
      state,
      action: PayloadAction<Objective>,
    ) => {
      if (state.objectives.length > 0) {
        const updatedObjectives = updateObjectiveInList(
          state.objectives,
          action.payload,
        )
        //@ts-ignore
        state.objectives = sortObjectivesByDefault(updatedObjectives)
      }
      if (state.objective?.id === action.payload.id) {
        //@ts-ignore
        state.objective = { ...state.objective, ...action.payload }
      }
      if (state.objectiveDetails?.id === action.payload.id) {
        //@ts-ignore
        state.objectiveDetails = { ...state.objectiveDetails, ...action.payload }
      }
      state.objectiveLoading = false
      state.objectiveError = undefined
    })
    builder.addCase(updateObjective.pending, (state) => {
      state.objectiveLoading = true
      state.objectiveError = undefined
    })
    builder.addCase(updateObjective.rejected, (state, action) => {
      state.objectiveLoading = false
      state.objectiveError = action.error
    })
    builder.addCase(addStep.fulfilled, (state, action: PayloadAction<Step>) => {
      const { objectiveId } = action.payload

      if (state.objective?.id === objectiveId) {
        //@ts-ignore
        state.objective = appendStepToObjective(state.objective, action.payload)
      }
      if (state.objectiveDetails?.id === objectiveId) {
        //@ts-ignore
        state.objectiveDetails = appendStepToObjective(
          state.objectiveDetails,
          action.payload,
        )
      }
      //@ts-ignore
      state.objectives = state.objectives.map((objective) =>
        objective.id === objectiveId
          ? appendStepToObjective(objective, action.payload)
          : objective,
      )
      state.stepLoading = false
      state.stepError = undefined
    })
    builder.addCase(addStep.pending, (state) => {
      state.stepLoading = true
      state.stepError = undefined
    })
    builder.addCase(addStep.rejected, (state, action) => {
      state.stepLoading = false
      state.stepError = action.error
    })
    builder.addCase(updateStep.fulfilled, (state, action: PayloadAction<Step>) => {
      if (state.objective?.id === action.payload.objectiveId) {
        //@ts-ignore
        state.objective = updateStepInObjective(state.objective, action.payload)
      }
      if (state.objectiveDetails?.id === action.payload.objectiveId) {
        //@ts-ignore
        state.objectiveDetails = updateStepInObjective(
          state.objectiveDetails,
          action.payload,
        )
      }
      //@ts-ignore
      state.objectives = state.objectives.map((objective) =>
        objective.id === action.payload.objectiveId
          ? updateStepInObjective(objective, action.payload)
          : objective,
      )

      state.stepLoading = false
      state.stepError = undefined
    })
    builder.addCase(updateStep.pending, (state) => {
      state.stepLoading = true
      state.stepError = undefined
    })
    builder.addCase(updateStep.rejected, (state, action) => {
      state.stepLoading = false
      state.stepError = action.error
    })
    builder.addCase(deleteStep.fulfilled, (state, action: PayloadAction<number>) => {
      const stepId = action.payload

      if (state.objective) {
        //@ts-ignore
        state.objective = removeStepFromObjective(state.objective, stepId)
      }
      if (state.objectiveDetails) {
        //@ts-ignore
        state.objectiveDetails = removeStepFromObjective(
          state.objectiveDetails,
          stepId,
        )
      }
      //@ts-ignore
      state.objectives = state.objectives.map((objective) =>
        objective.steps.some((step) => step.id === stepId)
          ? removeStepFromObjective(objective, stepId)
          : objective,
      )
      state.stepLoading = false
      state.stepError = undefined
    })
    builder.addCase(deleteStep.pending, (state) => {
      state.stepLoading = true
      state.stepError = undefined
    })
    builder.addCase(deleteStep.rejected, (state, action) => {
      state.stepLoading = false
      state.stepError = action.error
    })
  },

  // (builder) => {
  //     builder.addCase(getDevelopers.fulfilled, (state, action) => {
  //         state.developers = action.payload as IDeveloper[];
  //     })

  //     builder.addCase(addDeveloper.fulfilled, (state, action) => {
  //         state.developers.push(action.payload as IDeveloper);
  //     })

  //     builder.addCase(deleteDeveloper.fulfilled, (state, action) => {
  //         state.developers = state.developers.filter(developer => developer.id != action.payload)
  //     })
  // }
})

export const { setObjectives, setObjective, setObjectiveDetails } =
  objectiveSlice.actions
export default objectiveSlice.reducer
